// V4-FRONT-ANY-USAGE batch 5 2026-05-28
import { useRef, useEffect, useState, lazy, Suspense } from 'react';
import { MessageCircle, Send, RefreshCw, Check, Wand2, ArrowRight, ArrowLeft } from 'lucide-react';
import { Message, ChatRole, QuickReply, OnboardingStepId, OnboardingData } from '../../../types';
import { QuickReplies } from '../../common/QuickReplies';
import { OnboardingChecklist } from '../../common/OnboardingChecklist';
import { ActionButtonBase } from '../../common/ActionButtonBase';
import { Logger } from '../../../utils/logger';
import type { TranslationKeys } from '../../../i18n/fr';

// V4-FRONT-POSITIONING-MARKDOWN-LISTS 2026-07-15 — les bulles assistant rendent
// désormais le markdown COMPLET (gras, titres, listes à puces, blockquotes,
// tables) via react-markdown + remark-gfm, au lieu de l'ancien parser maison
// bold-only (`renderBoldMarkdown`) qui laissait `* item` / `> texte` en brut.
// Réutilise `MarkdownPreview` (styles scopés `.raiso-md`, XSS-safe, déjà utilisé
// par PositioningTextView), chargé en lazy pour ne pas gonfler le bundle chat.
const MarkdownPreview = lazy(() => import('../../common/MarkdownPreview'));

interface PositioningChatViewProps {
  messages: Message[];
  input: string;
  setInput: (value: string) => void;
  isStreaming: boolean;
  isExtracting: boolean;
  isLoadingSuggestion?: boolean;
  onboardingActive: boolean;
  currentStep: OnboardingStepId;
  onboardingData: OnboardingData;
  waitingForUrlInput: boolean;
  waitingForTextInput: boolean;
  currentInputPlaceholder: string;
  canGoBack?: boolean;
  handleSend: () => void;
  handleQuickReplySelect: (reply: QuickReply) => void;
  handlePreviousStep?: () => void;
  forceStartOnboarding: () => void;
  onApplyPositioning?: (content: string) => void;
  onApplySuggestion?: (instruction: string) => void;
  isTemplate?: boolean;
  t: TranslationKeys;
}

/**
 * Détecte si un contenu ressemble à un positionnement complet
 * (contient des titres markdown ## et au moins 2 sections)
 */
function detectPositioningContent(content: string): string | null {
  if (!content) return null;
  
  // Chercher un bloc de positionnement avec au moins 2 sections ##
  const sectionMatches = content.match(/^#{1,2}\s+.+$/gm);
  if (!sectionMatches || sectionMatches.length < 2) return null;
  
  // Vérifier que ça ressemble à un positionnement (contient des mots clés typiques)
  const positioningKeywords = ['RÔLE', 'LANGUE', 'VOIX', 'TON', 'ADN', 'PHILOSOPHIE', 'PROMPT', 'ROLE', 'VOICE', 'TONE'];
  const hasKeywords = positioningKeywords.some(kw => content.toUpperCase().includes(kw));
  
  if (!hasKeywords) return null;
  
  // Extraire le positionnement (tout à partir du premier # jusqu'à la fin ou jusqu'à un texte explicatif)
  const positioningStart = content.search(/^#\s+/m);
  if (positioningStart === -1) return null;
  
  return content.slice(positioningStart).trim();
}

/**
 * Détecte le bloc APPLY_SUGGESTION dans un message de l'assistant
 * Format: <!-- APPLY_SUGGESTION: {"instruction": "..."} -->
 * Retourne l'instruction si trouvée, null sinon
 */
function detectSuggestionBlock(content: string): string | null {
  if (!content) return null;
  
  const match = content.match(/<!--\s*APPLY_SUGGESTION:\s*(\{.*?\})\s*-->/s);
  if (!match) return null;
  
  try {
    const parsed = JSON.parse(match[1]);
    return parsed.instruction || null;
  } catch (e) {
    Logger.error('Failed to parse APPLY_SUGGESTION block:', e);
    return null;
  }
}

/**
 * Supprime le bloc APPLY_SUGGESTION du contenu affiché
 */
function cleanSuggestionBlock(content: string): string {
  return content.replace(/<!--\s*APPLY_SUGGESTION:\s*\{.*?\}\s*-->/gs, '').trim();
}

// V4-POS-SUGGESTION-DISMISS-PERSIST 2026-07-06 — les suggestions rejetées
// (« Continuer sans appliquer ») survivent désormais au reload : on persiste un
// hash djb2 du texte dans localStorage. Set borné (dernier N) → pas de croissance
// non bornée. Pas de backend nécessaire (état UI purement local, cf. backlog).
const DISMISSED_SUGGESTIONS_KEY = 'raiso_dismissed_pos_suggestions';
const DISMISSED_SUGGESTIONS_MAX = 50;

function hashSuggestion(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

function loadDismissedHashes(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_SUGGESTIONS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissedHashes(hashes: Set<string>): void {
  try {
    const arr = Array.from(hashes).slice(-DISMISSED_SUGGESTIONS_MAX);
    localStorage.setItem(DISMISSED_SUGGESTIONS_KEY, JSON.stringify(arr));
  } catch {
    /* localStorage indisponible (quota / SSR) — best-effort, non bloquant */
  }
}

/**
 * Vue chat du positionnement avec onboarding guidé
 * Extrait de PositioningTab pour améliorer la maintenabilité
 */
export function PositioningChatView({
  messages,
  input,
  setInput,
  isStreaming,
  isExtracting,
  isLoadingSuggestion = false,
  onboardingActive,
  currentStep,
  onboardingData,
  waitingForUrlInput,
  waitingForTextInput,
  currentInputPlaceholder,
  canGoBack = false,
  handleSend,
  handleQuickReplySelect,
  handlePreviousStep,
  forceStartOnboarding,
  onApplyPositioning,
  onApplySuggestion,
  isTemplate,
  t,
}: PositioningChatViewProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // V4-POS-SUGGESTION-DISMISS(-PERSIST) — on mémorise le HASH des suggestions
  // rejetées pour que « Continuer sans appliquer » masque durablement CETTE
  // suggestion. Une nouvelle suggestion (texte différent) s'affiche
  // naturellement ; la même ne réapparaît plus. Persisté en localStorage →
  // survit au reload (avant : state local perdu au refresh, la carte revenait).
  const [dismissedHashes, setDismissedHashes] = useState<Set<string>>(loadDismissedHashes);

  const dismissSuggestion = (text: string) => {
    const h = hashSuggestion(text);
    setDismissedHashes((prev) => {
      if (prev.has(h)) return prev;
      const next = new Set(prev);
      next.add(h);
      persistDismissedHashes(next);
      return next;
    });
  };
  // V4-POS-08 2026-05-29 — empêche le bouton "Générer mon positionnement" de
  // rester cliquable une fois la génération réussie. Détecté via la présence
  // d'un message `onboardingStep === 'complete'` OU un toast/state externe qui
  // bascule `onboardingActive` à false APRES qu'un summary ait été affiché.
  const hasGenerated = messages.some((m) => (m as Message & { onboardingStep?: string }).onboardingStep === 'complete');
  // Track si l'utilisateur est "stuck at bottom" — pattern chat standard.
  // Bug fix 19-05-2026 : avant, l'auto-scroll forçait scrollTop = scrollHeight
  // à CHAQUE update messages (y compris pendant le streaming d'une réponse),
  // ce qui rendait impossible de scroller manuellement vers le haut ET
  // empêchait parfois de voir le dernier message si la mesure du scroll
  // arrivait avant le rendu du DOM. On ne snappe désormais au fond que si
  // l'utilisateur y était déjà au moment de l'update.
  const stickToBottomRef = useRef(true);

  const SCROLL_BOTTOM_THRESHOLD_PX = 80;

  const isNearBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD_PX;
  };

  const handleScroll = () => {
    stickToBottomRef.current = isNearBottom();
  };

  // Scroll to bottom uniquement si l'utilisateur était déjà au fond.
  // Sinon on respecte sa position de lecture (il scrolle manuellement
  // l'historique pendant qu'un message stream).
  // V4-POS-05 2026-05-29 — fix auto-scroll : à chaque NOUVEAU message (delta
  // sur messages.length), on force le scroll en bas via scrollIntoView sur le
  // sentinel `messagesEndRef`. Plus fiable que `scrollTop = scrollHeight` (qui
  // peut être calculé avant que le DOM finisse de mesurer le dernier message,
  // laissant le textarea hors viewport). Pour les updates de content (streaming
  // d'une bulle déjà existante), on garde la logique de stickToBottom.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    // requestAnimationFrame pour laisser le DOM se mettre à jour avant scroll
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [messages.length]);

  // Pendant le streaming d'une bulle existante (même longueur de messages),
  // on snappe au fond sans animation pour éviter le scroll saccadé.
  useEffect(() => {
    if (chatContainerRef.current && stickToBottomRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // TODO: Amélioration future - Détection automatique des intentions de modification
  // Quand l'utilisateur demande explicitement une modification (verbes: "ajoute", "change", "modifie", "supprime"),
  // on pourrait déclencher directement /suggest-changes au lieu du chat normal pour un flux plus direct.
  // Voir: https://github.com/3615-future/rayso/issues/XXX

  // Detect last suggestion in message history
  const lastSuggestionMessage = [...messages].reverse().find(
    m => m.role === ChatRole.ASSISTANT && !m.isTyping && detectSuggestionBlock(m.content)
  );
  const lastSuggestionText = lastSuggestionMessage ? detectSuggestionBlock(lastSuggestionMessage.content) : null;
  const showSuggestionActions = !!lastSuggestionText && !isStreaming && !dismissedHashes.has(hashSuggestion(lastSuggestionText));

  return (
    // Fix UX 20-05 — `min-height: 0` (inline pour bypass Tailwind JIT non-géné)
    // indispensable sur toute la chaîne flex pour que le message area se
    // compresse et que la zone d'input reste visible peu importe le zoom
    // navigateur. Sans ça, à 125%+ zoom, l'input était poussé hors viewport.
    <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
      {/* Checklist de progression (visible seulement pendant l'onboarding) */}
      {onboardingActive && currentStep !== 'welcome' && (
        <div className="px-4 pt-4 flex-shrink-0">
          <OnboardingChecklist
            data={onboardingData}
            currentStep={currentStep}
            isActive={onboardingActive}
          />
        </div>
      )}

      {/* Messages — `min-height: 0` requis (inline) pour permettre la compression et le scroll */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-6 space-y-4"
        style={{ minHeight: 0 }}
      >
        {/* Message initial quand pas de messages */}
        {messages.length === 0 && (
          <div className="flex justify-start">
            <div className="flex items-start gap-3 max-w-[85%]">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <MessageCircle className="w-4 h-4" style={{ color: 'var(--color-text-light)' }} />
              </div>
              <div
                className="rounded-2xl rounded-tl-md px-4 py-3"
                style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
              >
                <Suspense fallback={null}>
                  <MarkdownPreview
                    content={
                      isTemplate === false
                        ? (t.positioning?.chatInitialMessageExisting || 'Bonjour ! Votre positionnement est déjà configuré. Je peux l\'améliorer, l\'affiner ou l\'enrichir. Que souhaitez-vous modifier ?')
                        : (t.positioning?.chatInitialMessage || 'Bonjour ! Je vais vous aider à définir votre positionnement...')
                    }
                  />
                </Suspense>
              </div>
            </div>
          </div>
        )}

        {messages.map((message, index) => {
          // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — `quickReplies` est déjà typé sur Message
          const positioningMessage = message;
          const isLastMessage = index === messages.length - 1;
          const hasQuickReplies = positioningMessage.quickReplies && positioningMessage.quickReplies.length > 0;
          
          // Détecter si le message contient un positionnement applicable (ancien système)
          const detectedPositioning = message.role === ChatRole.ASSISTANT && !message.isTyping
            ? detectPositioningContent(message.content)
            : null;
          
          // Détecter si le message contient un bloc APPLY_SUGGESTION (nouveau système)
          const suggestionInstruction = message.role === ChatRole.ASSISTANT && !message.isTyping
            ? detectSuggestionBlock(message.content)
            : null;
          
          // Nettoyer le contenu affiché (supprimer le bloc invisible)
          const displayContent = message.role === ChatRole.ASSISTANT
            ? cleanSuggestionBlock(message.content)
            : message.content;
          
          return (
            <div
              key={message.id}
              className={`flex ${message.role === ChatRole.USER ? 'justify-end' : 'justify-start'}`}
            >
              {/* Messages assistant */}
              {message.role === ChatRole.ASSISTANT && (
                <div className="flex flex-col items-start gap-3 max-w-[85%]">
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: 'var(--color-text-light)' }} />
                    </div>
                    <div
                      className="rounded-2xl rounded-tl-md px-4 py-3"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
                    >
                      {/* V4-FRONT-POSITIONING-MARKDOWN-LISTS — markdown complet (listes, blockquotes…) */}
                      <Suspense fallback={<p className="text-sm whitespace-pre-wrap">{displayContent}</p>}>
                        <MarkdownPreview content={displayContent} />
                      </Suspense>
                      {message.isTyping && (
                        <span className="inline-block w-1 h-4 animate-pulse ml-1" style={{ backgroundColor: 'var(--color-text-secondary)' }}></span>
                      )}
                    </div>
                  </div>
                  
                  {/* Bouton "Appliquer ces modifications" si suggestion détectée (nouveau système avec diff) */}
                  {suggestionInstruction && onApplySuggestion && !showSuggestionActions && (
                    <div className="ml-11">
                      <ActionButtonBase
                        onClick={() => onApplySuggestion(suggestionInstruction)}
                        disabled={isLoadingSuggestion}
                        loading={isLoadingSuggestion}
                        icon={Wand2}
                        label={isLoadingSuggestion
                          ? (t.positioning?.generatingChanges || 'Génération...')
                          : (t.positioning?.applySuggestion || 'Appliquer ces modifications')
                        }
                        appearance="filled"
                        variant="default"
                        revealLabelOnHover={true}
                        expanded={false}
                        className="h-8"
                        title={t.positioning?.applySuggestion || 'Appliquer ces modifications'}
                      />
                    </div>
                  )}
                  
                  {/* Bouton Appliquer si positionnement complet détecté (ancien système) */}
                  {!suggestionInstruction && detectedPositioning && onApplyPositioning && (
                    <div className="ml-11">
                      <ActionButtonBase
                        onClick={() => onApplyPositioning(detectedPositioning)}
                        icon={Check}
                        label={t.positioning?.applyThisPositioning || 'Appliquer ce positionnement'}
                        appearance="filled"
                        variant="default"
                        expanded={true}
                        revealLabelOnHover={false}
                        className="h-8 px-4"
                      />
                    </div>
                  )}
                  
                  {/* QuickReplies — V4-POS-08 2026-05-29 : on bloque les replies
                      si la génération a déjà eu lieu (sécurité belt+suspenders
                      même si onboardingActive devrait suffire). */}
                  {isLastMessage && hasQuickReplies && onboardingActive && !hasGenerated && (
                    <div className="ml-11">
                      <QuickReplies
                        replies={positioningMessage.quickReplies ?? []}
                        onSelect={handleQuickReplySelect}
                        disabled={isStreaming || isExtracting}
                      />
                    </div>
                  )}

                  {/* Retour arrière dans le wizard (V4-POS-WIZARD-BACK). Affiché sur le
                      dernier message tant que l'onboarding est actif et qu'un historique existe. */}
                  {isLastMessage && onboardingActive && !hasGenerated && canGoBack && handlePreviousStep && (
                    <div className="ml-11 mt-2">
                      <button
                        type="button"
                        onClick={handlePreviousStep}
                        disabled={isStreaming || isExtracting}
                        className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 transition-colors"
                        style={{ color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                      >
                        <ArrowLeft className="w-3 h-3" />
                        {t.tour?.back ?? 'Précédent'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Messages utilisateur */}
              {message.role === ChatRole.USER && (
                <div
                  className="max-w-[80%] rounded-2xl rounded-tr-md px-4 py-3"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Zone d'input — Fix UX 20-05 : `sticky bottom-0` en safety net si jamais
          le calcul flex échoue à un zoom extrême. Le background opaque évite
          que les messages transparaissent sous l'input. */}
      {(!onboardingActive || waitingForUrlInput || waitingForTextInput) && (
        <div
          className="p-4 flex-shrink-0 sticky bottom-0 z-10"
          style={{
            borderTop: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-background)',
          }}
        >
          {showSuggestionActions ? (
            <div className="flex gap-2">
              <ActionButtonBase
                onClick={() => { onApplySuggestion!(lastSuggestionText!); dismissSuggestion(lastSuggestionText!); }}
                disabled={isLoadingSuggestion}
                loading={isLoadingSuggestion}
                icon={Wand2}
                label={t.positioning?.applySuggestion || 'Appliquer ces modifications'}
                appearance="filled"
                variant="default"
                expanded={true}
                revealLabelOnHover={false}
                className="flex-1 h-10"
              />
              <ActionButtonBase
                onClick={() => dismissSuggestion(lastSuggestionText!)}
                icon={ArrowRight}
                label={t.positioning?.discardSuggestion || 'Continuer sans appliquer'}
                appearance="outline"
                variant="secondary"
                expanded={true}
                revealLabelOnHover={false}
                className="flex-1 h-10"
              />
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                waitingForUrlInput 
                  ? currentInputPlaceholder || 'https://...'
                  : waitingForTextInput
                    ? currentInputPlaceholder || 'Votre réponse...'
                    : t.positioning?.chatPlaceholder || 'Écrivez votre message...'
              }
              className="flex-1 px-4 py-2 rounded-2xl focus:outline-none resize-none"
              style={{
                backgroundColor: 'var(--color-surface-hover)',
                border: `1px solid ${waitingForUrlInput || waitingForTextInput ? 'var(--color-primary)' : 'var(--color-border)'}`,
                minHeight: '40px',
                maxHeight: '150px',
                // Inline (Tailwind figé) : permet le scroll quand le texte dépasse 150px.
                overflowY: 'auto',
              }}
              disabled={isStreaming || isExtracting}
              rows={1}
              autoFocus={waitingForUrlInput || waitingForTextInput}
            />
            <ActionButtonBase
              onClick={handleSend}
              disabled={!input.trim() || isStreaming || isExtracting}
              loading={isStreaming || isExtracting}
              icon={Send}
              appearance="filled"
              variant="custom"
              borderColor={(!input.trim() || isStreaming || isExtracting) ? 'var(--color-border)' : 'var(--color-primary)'}
              hoverBgColor={(!input.trim() || isStreaming || isExtracting) ? 'var(--color-surface)' : 'var(--color-primary)'}
              iconColor={(!input.trim() || isStreaming || isExtracting) ? 'var(--color-text-secondary)' : 'var(--color-button-text)'}
              textColorOnHover={(!input.trim() || isStreaming || isExtracting) ? 'var(--color-text-secondary)' : 'var(--color-button-text)'}
              className="w-10 h-10"
              revealLabelOnHover={false}
              ariaLabel={t.positioning?.sendMessage || 'Envoyer le message'}
            />
          </div>
        )}
        </div>
      )}
    </div>
  );
}
